import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import Button from 'components/base/Button';
import Modal from 'react-bootstrap/Modal';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faWindowClose } from '@fortawesome/free-solid-svg-icons';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { updateConfig, createConfig } from '../../../../redux/slices/configSlice';
import { PrioritizedWorker } from '../../../../components/nors/WorkerListWithPriority';

import SMTPFields from './SMTPFields';
import GeneralFields from './GeneralFields';
import RetentionPoliciesFields from './RetentionPoliciesFields';
import DefaultTimezoneFields from './DefaultTimezoneFields';
import { isRetentionPoliciesBE } from 'utils/retentionPolicies';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
interface EditConfigItemProps {
  configIndex: number | null;
  isNew?: boolean;
  handleClose: () => void;
}

const EditConfigItem: React.FC<EditConfigItemProps> = ({ configIndex, isNew = false, handleClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentConfig = useAppSelector((state) => (configIndex !== null ? state.config.items[configIndex] : null));

  const [selectedKey, setSelectedKey] = useState<string>(isNew ? '' : currentConfig?.key || '');
  const [jsonValid, setJsonValid] = useState<boolean>(true);
  const [formData, setFormData] = useState<any>(currentConfig?.value || {});
  const [timezoneValidationMessage, setTimezoneValidationMessage] = useState<string>('');
  // Config.value is typed as `object` in the slice because each config
  // key has a different value shape. Tightening it to a discriminated
  // union per key belongs to step 3 PR D.
  const [selectedWorkers, setSelectedWorkers] = useState<PrioritizedWorker[]>(
    (currentConfig?.value as { workers?: PrioritizedWorker[] } | undefined)?.workers || []
  );
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const currentFormValues = useMemo(() => ({ selectedKey, formData, selectedWorkers }), [selectedKey, formData, selectedWorkers]);
  const isDirtyRaw = useUnsavedChanges(true, currentFormValues);
  const isDirty = !isNew && isDirtyRaw;

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (!isNew && currentConfig) {
      setFormData(currentConfig?.value || {});
    }
  }, [currentConfig, isNew]);

  // If switching to retention_policies, ensure BE object shape in state
  useEffect(() => {
    if (selectedKey === 'retention_policies') {
      if (!isRetentionPoliciesBE(formData)) setFormData({});
      setJsonValid(true);
    }

    if (selectedKey === 'default_timezone') {
      const timezone = typeof formData === 'string' ? formData : typeof formData?.timezone === 'string' ? formData.timezone : 'UTC';

      setFormData({
        timezone: timezone || 'UTC',
      });
      applyDefaultTimezoneValidation(timezone || 'UTC');
      return;
    }

    setTimezoneValidationMessage('');
    setJsonValid(true);
    // eslint-disable-next-line
  }, [selectedKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const applyDefaultTimezoneValidation = (timezoneValue: string) => {
    const timezone = timezoneValue.trim();

    if (!timezone) {
      setJsonValid(false);
      setTimezoneValidationMessage('Timezone is required.');
      return false;
    }

    if (!moment.tz.zone(timezone)) {
      setJsonValid(false);
      setTimezoneValidationMessage('Invalid timezone. Use a valid IANA value like UTC or Europe/Amsterdam.');
      return false;
    }

    setJsonValid(true);
    setTimezoneValidationMessage('');
    return true;
  };

  const handleDefaultTimezoneChange = (value: string) => {
    setFormData((prevData: any) => ({
      ...(prevData || {}),
      timezone: value,
    }));
    applyDefaultTimezoneValidation(value);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!jsonValid) return;

    const valueToSave =
      selectedKey === 'default_timezone'
        ? {
            timezone: typeof formData?.timezone === 'string' && formData.timezone.trim().length > 0 ? formData.timezone.trim() : 'UTC',
          }
        : formData;

    if (selectedKey === 'default_timezone' && !applyDefaultTimezoneValidation(valueToSave.timezone)) {
      return;
    }

    const updatedConfig = {
      key: selectedKey,
      value: valueToSave,
    };

    if (isNew) {
      dispatch(createConfig(updatedConfig));
    } else {
      dispatch(updateConfig({ config_id: currentConfig.config_id, ...updatedConfig }));
    }

    handleClose();
  };

  const renderFields = () => {
    if (selectedKey === 'smtp') {
      return <SMTPFields formData={formData} onInputChange={handleInputChange} selectedWorkers={selectedWorkers} setSelectedWorkers={setSelectedWorkers} />;
    }

    if (selectedKey === 'retention_policies') {
      return (
        <RetentionPoliciesFields
          selectedKey={selectedKey}
          setSelectedKey={setSelectedKey}
          formData={formData}
          setFormData={setFormData}
          setJsonValid={setJsonValid}
        />
      );
    }

    if (selectedKey === 'default_timezone') {
      return (
        <DefaultTimezoneFields
          selectedKey={selectedKey}
          setSelectedKey={setSelectedKey}
          formData={formData}
          onTimezoneChange={handleDefaultTimezoneChange}
          jsonValid={jsonValid}
          validationMessage={timezoneValidationMessage}
        />
      );
    }

    return (
      <GeneralFields
        selectedKey={selectedKey}
        setSelectedKey={setSelectedKey}
        formData={formData}
        setFormData={setFormData}
        jsonValid={jsonValid}
        setJsonValid={setJsonValid}
      />
    );
  };

  const handleConfigTypeChange = (value: string) => {
    setSelectedKey(value);

    if (value === 'default_timezone') {
      setFormData({ timezone: 'UTC' });
      setTimezoneValidationMessage('');
      setJsonValid(true);
      return;
    }

    if (value === 'retention_policies') {
      setFormData({});
      setTimezoneValidationMessage('');
      setJsonValid(true);
    }
  };

  return (
    <Modal show={true} onHide={handleRequestClose} backdrop="static" keyboard={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {isNew ? t('button.newe') + ' ' + t('menu.configuration') + ' ' + t('key') : t('button.edit') + ' ' + t('menu.configuration') + ' ' + t('key')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isNew ? (
          <>
            <Form.Group as={Row} className="mb-3" controlId="formConfigKey">
              <Form.Label column sm="2">
                Config {t('key')}
              </Form.Label>
              <Col sm="10">
                <Form.Control type="text" defaultValue={currentConfig?.key} plaintext readOnly />
              </Col>
            </Form.Group>

            {renderFields()}
          </>
        ) : (
          <>
            <Form.Group as={Row} className="mb-3" controlId="configType">
              <Form.Label column sm="2">
                Config Type
              </Form.Label>
              <Col sm="10">
                <Form.Control as="select" value={selectedKey} onChange={(e) => handleConfigTypeChange(e.target.value)}>
                  <option value="">
                    {t('select')} {t('menu.configuration')} Type
                  </option>
                  <option value="smtp">SMTP</option>
                  <option value="default_timezone">Default Timezone</option>
                  <option value="retention_policies">Retention Policies</option>
                  <option value="general">{t('general')}</option>
                </Form.Control>
              </Col>
            </Form.Group>

            {renderFields()}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="phoenix-danger" onClick={handleRequestClose} startIcon={<FontAwesomeIcon icon={faWindowClose} />}>
          {t('button.close')}
        </Button>
        <Button variant="phoenix-primary" onClick={handleSave} startIcon={<FontAwesomeIcon icon={faSave} />} disabled={!jsonValid}>
          {t('button.saveAndClose')}
        </Button>
      </Modal.Footer>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          handleClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default EditConfigItem;
